import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import { Box, Button, IconButton, InputBase, Popover } from '@mui/material'
import type { InputBaseComponentProps, SxProps, Theme } from '@mui/material'
import moment from 'moment'
import { muahangKiotLike } from '../../../layout/muahangKiotTokens'
import { normalizeTime24h } from '../../../../utils/commonUtils'
import { resolveFieldSurface } from '../FieldSurfaceContext'
import { getFieldSurfaceStyle } from '../fieldSurfaces/registry'
import { KIOT_FORM_FIELD_CLASS, mergeSx } from '../formFieldStyles'
import type { FieldSurface } from '../fieldSurfaces/types'
import { DateCalendar } from './DateCalendar'
import { TimePicker24 } from './TimePicker24'
import {
  DATE_ISO,
  dateTimePopoverPaperSx,
  formatDateDisplay,
  formatDateTimeDisplay,
  joinLocalDatetime,
  maskDateDraft,
  maskDateTimeDraft,
  maskTimeDraft,
  parseDateInputToIso,
  parseDateTimeDraft,
  splitLocalDatetime,
  todayIsoDate,
} from './dateTimeUtils'

export type DateTimeFieldControlProps = {
  /** Chuỗi local `YYYY-MM-DDTHH:mm` (24h). */
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  required?: boolean
  error?: boolean
  fullWidth?: boolean
  id?: string
  surface?: FieldSurface
  sx?: SxProps<Theme>
  /** Chỉ hiện phần ngày (ẩn giờ). */
  dateOnly?: boolean
  /** Chỉ hiện phần giờ (ẩn ngày). */
  timeOnly?: boolean
  /**
   * Hiện icon mở dropdown (lịch / đồng hồ).
   * Mặc định `true` — truyền `false` để ẩn.
   */
  showIcon?: boolean
  /** Chữ cố định sát trước icon (vd. `23:59` giờ chốt — không chọn được). */
  endText?: string
  /**
   * Mốc sớm nhất `YYYY-MM-DDTHH:mm`.
   * Giờ chọn ra trước mốc thì không ghi nhận (giữ giá trị cũ).
   * Ngày trước mốc vẫn ghi nhận để ô cảnh báo.
   */
  min?: string
  /** Gắn thêm lên `<input>` (vd. `data-excel-row` cho bảng Excel). */
  inputProps?: InputBaseComponentProps
  /**
   * Keydown trên ô nhập — gọi trước handler nội bộ.
   * Nếu `preventDefault()` thì bỏ xử lý Enter mặc định (dùng cho Tab/Enter trên Excel).
   */
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

/**
 * Base control chọn ngày + giờ 24h.
 * - 1 ô gõ: `dd/MM/yyyy HH:mm` (bôi đen cả khi focus để nhập lại)
 * - Icon / F4 / double-click: mở dropdown gồm cả lịch + giờ
 */
export function DateTimeFieldControl({
  value = '',
  onChange,
  disabled,
  required,
  error,
  fullWidth,
  id: idProp,
  surface: surfaceProp,
  sx,
  dateOnly,
  timeOnly,
  showIcon = true,
  endText,
  min,
  inputProps,
  onInputKeyDown,
}: DateTimeFieldControlProps) {
  const autoId = useId()
  const id = idProp ?? autoId
  const surface = resolveFieldSurface(surfaceProp)
  const surfaceStyle = getFieldSurfaceStyle(surface)
  const isUnderline = surfaceStyle.inputChrome === 'underline'
  const h = surfaceStyle.inputHeightPx

  const rootRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const showDate = !timeOnly
  const showTime = !dateOnly
  const combined = showDate && showTime

  const { date, time } = useMemo(() => splitLocalDatetime(String(value ?? '')), [value])
  const minNorm = (min ?? '').trim().slice(0, 16)

  /** Ghép ngày + giờ có trước `min` không. */
  const blockedByMin = (dateIso: string, hhmm: string) => {
    if (minNorm.length < 16 || !dateIso) return false
    const t = normalizeTime24h(hhmm) || '00:00'
    return joinLocalDatetime(dateIso, t) < minNorm
  }

  /**
   * Giờ trên một ngày không sớm hơn mốc → bỏ, không ghi nhận.
   * Ngày sớm hơn mốc vẫn cho ghi (cảnh báo ở ô).
   */
  const rejectPastTime = (dateIso: string, hhmm: string) => {
    if (!blockedByMin(dateIso, hhmm)) return false
    const minDate = minNorm.slice(0, 10)
    return !minDate || dateIso >= minDate
  }

  const displayFromValue = () => {
    if (combined) return formatDateTimeDisplay(date, time)
    if (showDate) return formatDateDisplay(date)
    return time || ''
  }

  const [draft, setDraft] = useState(displayFromValue)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setDraft(displayFromValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync từ value
  }, [date, time, combined, showDate])

  const emit = (nextDate: string, nextTime: string) => {
    if (!onChange) return
    if (timeOnly) {
      const t = normalizeTime24h(nextTime) || '00:00'
      onChange(joinLocalDatetime(todayIsoDate(), t))
      return
    }
    if (!nextDate) {
      onChange('')
      return
    }
    // dateOnly → chỉ `YYYY-MM-DD` (không kèm giờ).
    if (dateOnly) {
      onChange(nextDate)
      return
    }
    const t = normalizeTime24h(nextTime) || '00:00'
    onChange(joinLocalDatetime(nextDate, t))
  }

  const openPicker = () => {
    if (disabled) return
    setOpen(true)
  }

  const closePicker = () => setOpen(false)

  const commitDraft = () => {
    if (timeOnly) {
      const normalized =
        normalizeTime24h(draft) || normalizeTime24h(maskTimeDraft(draft)) || ''
      if (normalized && rejectPastTime(todayIsoDate(), normalized)) {
        setDraft(displayFromValue())
        return
      }
      setDraft(normalized)
      if (normalized) emit(todayIsoDate(), normalized)
      else setDraft(time || '')
      return
    }
    if (!draft.trim()) {
      if (date) emit('', '')
      return
    }
    if (dateOnly) {
      const iso = parseDateInputToIso(draft)
      if (!iso) {
        setDraft(formatDateDisplay(date))
        return
      }
      setDraft(formatDateDisplay(iso))
      // Trùng giá trị → không emit (tránh re-render làm mất focus Tab trên Excel).
      if (iso !== date) emit(iso, '00:00')
      return
    }
    const { dateIso, time: t } = parseDateTimeDraft(draft)
    if (!dateIso) {
      setDraft(displayFromValue())
      return
    }
    const hhmm = normalizeTime24h(t) || time || '00:00'
    if (rejectPastTime(dateIso, hhmm)) {
      setDraft(displayFromValue())
      return
    }
    setDraft(formatDateTimeDisplay(dateIso, hhmm))
    emit(dateIso, hhmm)
  }

  const applyDate = (iso: string) => {
    const fromDraft = parseDateTimeDraft(draft)
    const hhmm =
      normalizeTime24h(time) ||
      normalizeTime24h(fromDraft.time) ||
      normalizeTime24h(draft) ||
      '00:00'
    setDraft(combined ? formatDateTimeDisplay(iso, hhmm) : formatDateDisplay(iso))
    emit(iso, showTime ? hhmm : '00:00')
    // Chỉ ngày: chọn xong đóng dropdown.
    if (dateOnly) closePicker()
  }

  const applyTime = (hhmm: string) => {
    const next = normalizeTime24h(hhmm) || hhmm
    const activeDate = timeOnly ? todayIsoDate() : parseDateInputToIso(draft) || date
    if (activeDate && rejectPastTime(activeDate, next)) {
      setDraft(displayFromValue())
      return
    }
    if (timeOnly) {
      setDraft(next)
      emit(todayIsoDate(), next)
      return
    }
    // Chỉ set giờ — không tự tạo ngày nếu chưa có.
    if (!activeDate) {
      setDraft(combined ? formatDateTimeDisplay('', next).trim() : next)
      return
    }
    setDraft(combined ? formatDateTimeDisplay(activeDate, next) : next)
    emit(activeDate, next)
  }

  const pickTodayDate = () => {
    applyDate(moment().format(DATE_ISO))
  }

  const pickNowTime = () => {
    const now = moment()
    const exact = `${String(now.hour()).padStart(2, '0')}:${String(now.minute()).padStart(2, '0')}`
    applyTime(exact)
  }

  const onDraftChange = (raw: string) => {
    if (timeOnly) {
      const next = maskTimeDraft(raw)
      if (next.length === 5) {
        const n = normalizeTime24h(next)
        if (n && rejectPastTime(todayIsoDate(), n)) {
          setDraft(displayFromValue())
          return
        }
        setDraft(next)
        if (n) emit(todayIsoDate(), n)
        return
      }
      setDraft(next)
      return
    }
    if (dateOnly) {
      const next = maskDateDraft(raw)
      setDraft(next)
      if (next.replace(/\D/g, '').length === 8) {
        const iso = parseDateInputToIso(next)
        if (iso) {
          setDraft(formatDateDisplay(iso))
          emit(iso, '00:00')
        }
      }
      return
    }
    const next = maskDateTimeDraft(raw)
    setDraft(next)
    const digits = next.replace(/\D/g, '')
    if (digits.length === 12) {
      const { dateIso, time: t } = parseDateTimeDraft(next)
      if (dateIso && t) {
        if (rejectPastTime(dateIso, t)) {
          setDraft(displayFromValue())
          return
        }
        setDraft(formatDateTimeDisplay(dateIso, t))
        emit(dateIso, t)
      }
    }
  }

  const pickerDate = parseDateInputToIso(draft) || date
  const minDatePart = minNorm.slice(0, 10)
  const minTimePart = minNorm.slice(11, 16)
  const disabledTimeBefore =
    minNorm.length < 16 || !pickerDate
      ? undefined
      : pickerDate < minDatePart
        ? '24:00'
        : pickerDate === minDatePart
          ? minTimePart
          : undefined

  const placeholder = combined ? 'dd/mm/yyyy HH:mm' : showDate ? 'dd/mm/yyyy' : 'HH:mm'
  const maxLen = combined ? 16 : showDate ? 10 : 5
  // Bề rộng chữ tính theo `ch` của font ô nhập (số tabular = 1ch, «/ : space» hẹp hơn)
  // để icon nằm sát chữ ở mọi cỡ font; placeholder (chữ dd/mm…) rộng hơn giá trị số.
  const valueCh = combined ? 15 : showDate ? 10.5 : 5
  const placeholderCh = combined ? 17 : showDate ? 11.2 : 6.4
  const textCh = draft.trim() ? valueCh : placeholderCh
  const endTextTrim = (endText ?? '').trim()
  /** `ch` tabular ≈ đủ chỗ số + khoảng cách với icon (vd. `23:59`). */
  const endTextCh = endTextTrim ? endTextTrim.length * 0.95 + 1.6 : 0
  const iconW = showIcon ? 28 : 0
  const gapPx = showIcon || endTextTrim ? 4 : 0
  // Padding ngang trong border — icon sát mép phải (pr nhỏ).
  const padX = isUnderline ? 0 : 8 + (showIcon ? 4 : 8)
  const textW = `${textCh}ch`
  const controlW = `calc(${textCh + endTextCh}ch + ${gapPx + iconW + padX}px)`
  const PickerIcon = timeOnly ? AccessTimeOutlinedIcon : CalendarMonthOutlinedIcon

  const inputSx = {
    p: 0,
    m: 0,
    flex: 1,
    minWidth: 0,
    fontFamily: surfaceStyle.fontFamily,
    fontSize: `${surfaceStyle.inputFontSizePx}px`,
    fontWeight: surfaceStyle.inputFontWeight,
    color: error ? 'error.main' : surfaceStyle.inputColor,
    opacity: disabled ? 0.6 : 1,
    '& .MuiInputBase-input': {
      p: 0,
      m: 0,
      height: h,
      boxSizing: 'border-box',
      fontVariantNumeric: 'tabular-nums',
      fontFamily: surfaceStyle.fontFamily,
      fontSize: `${surfaceStyle.inputFontSizePx}px`,
      fontWeight: surfaceStyle.inputFontWeight,
      color: error ? 'error.main' : surfaceStyle.inputColor,
      letterSpacing: '0.01em',
      cursor: disabled ? 'default' : 'text',
      '&::placeholder': {
        color: surfaceStyle.placeholderColor,
        opacity: 1,
      },
    },
  } as const

  return (
    <Box
      className={KIOT_FORM_FIELD_CLASS}
      id={id}
      sx={mergeSx(
        {
          // fullWidth: co theo cột form — không ép minWidth chữ+icon (tránh lệch dòng / tràn ô).
          width: fullWidth ? '100%' : controlW,
          minWidth: fullWidth ? 0 : controlW,
          maxWidth: fullWidth ? '100%' : controlW,
          flexShrink: fullWidth ? 1 : 0,
          // `ch` của wrapper phải cùng font với ô nhập.
          fontFamily: surfaceStyle.fontFamily,
          fontSize: `${surfaceStyle.inputFontSizePx}px`,
        },
        sx,
      )}
    >
      <Box
        ref={rootRef}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: `${gapPx}px`,
          width: fullWidth ? '100%' : controlW,
          minWidth: fullWidth ? 0 : controlW,
          maxWidth: fullWidth ? '100%' : controlW,
          height: h,
          boxSizing: 'border-box',
          overflow: 'hidden',
          ...(isUnderline
            ? {
                borderBottom: `1px solid ${surfaceStyle.borderColor}`,
                borderBottomColor: error ? 'error.main' : surfaceStyle.borderColor,
                '&:hover': {
                  borderBottomColor: error ? 'error.main' : surfaceStyle.borderHover,
                },
                '&:focus-within': {
                  borderBottomColor: error ? 'error.main' : surfaceStyle.focusColor,
                },
              }
            : {
                border: `1px solid ${surfaceStyle.borderColor}`,
                borderColor: error ? 'error.main' : surfaceStyle.borderColor,
                borderRadius: `${surfaceStyle.borderRadiusPx}px`,
                bgcolor: surfaceStyle.inputBg,
                pl: 1,
                pr: showIcon ? 0.25 : 1,
                '&:hover': {
                  borderColor: error ? 'error.main' : surfaceStyle.borderHover,
                },
                '&:focus-within': {
                  borderColor: error ? 'error.main' : surfaceStyle.focusColor,
                  boxShadow: surfaceStyle.focusShadow,
                },
              }),
        }}
      >
        <InputBase
          inputRef={inputRef}
          id={`${id}-value`}
          value={draft}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onDraftChange(e.target.value)}
          onFocus={(e) => {
            // Bôi đen cả ngày + giờ để nhập lại.
            e.target.select()
          }}
          onBlur={commitDraft}
          onDoubleClick={openPicker}
          onKeyDown={(e) => {
            if (e.key === 'F4') {
              e.preventDefault()
              openPicker()
              return
            }
            onInputKeyDown?.(e)
            if (e.defaultPrevented) return
            if (e.key === 'Enter') {
              e.preventDefault()
              commitDraft()
              ;(e.target as HTMLInputElement).blur()
            }
          }}
          inputProps={{
            inputMode: 'numeric',
            maxLength: maxLen,
            'aria-label': combined ? 'Ngày giờ' : showDate ? 'Ngày' : 'Giờ (24h)',
            'aria-required': required,
            ...inputProps,
          }}
          sx={{
            ...inputSx,
            width: fullWidth ? undefined : textW,
            minWidth: fullWidth ? 0 : textW,
            maxWidth: fullWidth ? '100%' : textW,
            flex: fullWidth ? 1 : undefined,
          }}
        />

        {endTextTrim ? (
          <Box
            component="span"
            aria-hidden
            sx={{
              flexShrink: 0,
              fontFamily: surfaceStyle.fontFamily,
              fontSize: `${surfaceStyle.inputFontSizePx}px`,
              fontWeight: 600,
              color: error ? 'error.main' : surfaceStyle.inputColor,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              userSelect: 'none',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              mr: showIcon ? 0.25 : 0,
            }}
          >
            {endTextTrim}
          </Box>
        ) : null}

        {showIcon ? (
          <IconButton
            size="small"
            disabled={disabled}
            aria-label="Chọn ngày giờ"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openPicker}
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              p: 0,
              mr: 0,
              color: error ? 'error.main' : muahangKiotLike.kiotTextMuted,
              '&:hover': { color: error ? 'error.main' : muahangKiotLike.linkBlue },
            }}
          >
            <PickerIcon sx={{ fontSize: 18, color: 'inherit' }} />
          </IconButton>
        ) : null}
      </Box>

      <Popover
        open={open}
        anchorEl={rootRef.current}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              ...dateTimePopoverPaperSx,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          {showDate ? (
            <DateCalendar
              value={parseDateInputToIso(draft) || date}
              onPick={applyDate}
              onClose={closePicker}
              closeOnPick={false}
              showActions={false}
            />
          ) : null}
          {showTime ? (
            <Box
              sx={{
                borderLeft: showDate
                  ? `1px solid ${muahangKiotLike.filterPanelBorder}`
                  : 'none',
                display: 'flex',
              }}
            >
              <TimePicker24
                value={
                  normalizeTime24h(parseDateTimeDraft(draft).time) ||
                  normalizeTime24h(time) ||
                  normalizeTime24h(draft) ||
                  '00:00'
                }
                onChange={applyTime}
                onClose={closePicker}
                closeOnPick={false}
                showActions={false}
                disabledBefore={disabledTimeBefore}
              />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            p: 0.75,
            borderTop: `1px solid ${muahangKiotLike.filterPanelBorder}`,
            bgcolor: '#f7f9fc',
          }}
        >
          {showDate ? (
            <Button
              size="small"
              variant="outlined"
              onClick={pickTodayDate}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                minHeight: 30,
                borderRadius: '6px',
                fontFamily: muahangKiotLike.kiotFontStack,
                flex: 1,
              }}
            >
              Hôm nay
            </Button>
          ) : null}
          {showTime ? (
            <Button
              size="small"
              variant="outlined"
              onClick={pickNowTime}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 12,
                minHeight: 30,
                borderRadius: '6px',
                fontFamily: muahangKiotLike.kiotFontStack,
                flex: 1,
              }}
            >
              Bây giờ
            </Button>
          ) : null}
          <Button
            size="small"
            variant="contained"
            onClick={closePicker}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 12,
              minHeight: 30,
              borderRadius: '6px',
              boxShadow: 'none',
              fontFamily: muahangKiotLike.kiotFontStack,
              flex: 1,
            }}
          >
            Xong
          </Button>
        </Box>
      </Popover>
    </Box>
  )
}

export default DateTimeFieldControl
